/**
 * Entry point for the embeddable Neutrix voice agent (v1.1).
 *
 * Single embed pattern:
 *
 *   <div data-neutrix-agent="inline" style="aspect-ratio:1/1; max-width:560px"></div>
 *   <script src="https://agent.neutrix.co/widget.js" defer></script>
 *
 * The script registers <neutrix-voice-agent> once and walks the document for
 * any matching slots, mounting one component per slot. Re-runs on
 * DOMContentLoaded too in case the script is injected before the slots exist.
 *
 * Killswitch: host pages can set `window.NEUTRIX_AGENT_DISABLED = true` before
 * this script loads to skip mounting entirely.
 */

import { NeutrixVoiceAgent } from './component';
import { mountInlineSurfaces } from './modes/inline';

if (typeof window !== 'undefined' && window.NEUTRIX_AGENT_DISABLED === true) {
  // eslint-disable-next-line no-console
  console.info('[neutrix-voice-agent] disabled via window.NEUTRIX_AGENT_DISABLED');
} else {
  bootstrap();
}

function bootstrap(): void {
  if (!customElements.get('neutrix-voice-agent')) {
    customElements.define('neutrix-voice-agent', NeutrixVoiceAgent);
  }

  const mount = (): void => {
    mountInlineSurfaces(document);
  };

  mount();

  // If the script was injected before the matching slot existed (e.g. in
  // <head> with `defer` but a single-page app injects the slot later), retry
  // on DOMContentLoaded. Idempotent — mountInlineSurfaces marks slots it has
  // already handled.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount, { once: true });
  }
}

declare global {
  interface Window {
    NEUTRIX_AGENT_DISABLED?: boolean;
  }
}
